import { useEffect, useMemo, useState } from "react";
import { addDays, addMonths, endOfMonth, format, startOfMonth, startOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const ymd = (d) => format(d, "yyyy-MM-dd");

const Dia = ({ d, mes, sel, data, profs, onPick, onDrop }) => {
  const f = ymd(d);
  const tot = Object.values(data || {}).reduce((s, x) => ({ c: s.c + x.citas, l: s.l + x.libres }), { c: 0, l: 0 });
  const fuera = d.getMonth() !== mes || d.getDay() === 0;
  return (
    <button type="button" onClick={() => onPick(d)} onMouseUp={() => onDrop(f)} data-testid={`mes-dia-${f}`}
      className={`h-9 rounded border px-px text-left leading-none transition-colors hover:border-[#1E3A8A] ${sel ? "border-[#1E3A8A] bg-sky-50" : "border-slate-100"} ${fuera ? "opacity-40" : ""}`}>
      <div className="text-[9px] font-semibold text-slate-700">{d.getDate()}</div>
      {(tot.c > 0 || tot.l > 0) && <div className="text-[8px] leading-tight whitespace-nowrap"><b className="text-slate-800">{tot.c}</b><span className="text-slate-400">/</span><b className="text-emerald-700">{tot.l}</b></div>}
      <div className="flex flex-wrap gap-px mt-0.5">{profs.filter((p) => data?.[p.id]).map((p) => <span key={p.id} className="h-1 w-1 rounded-full" style={{ background: p.color }} />)}</div>
    </button>
  );
};

export const MesLateral = ({ date, centro, profs, reloadKey, onPick, onDrop }) => {
  const [mes, setMes] = useState(startOfMonth(date));
  const [data, setData] = useState({});
  useEffect(() => {
    setMes(startOfMonth(date));
  }, [date]);
  const dias = useMemo(() => {
    const ini = startOfWeek(mes, { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, i) => addDays(ini, i)).filter((d, i) => i < 35 || d <= endOfMonth(mes));
  }, [mes]);
  useEffect(() => {
    api.get("/agenda-mes", { params: { desde: ymd(dias[0]), hasta: ymd(dias[dias.length - 1]), centro } }).then((r) => setData(r.data));
  }, [dias, centro, reloadKey]);
  const sel = data[ymd(date)] || {};
  return (
    <aside className="bg-white border border-slate-200 rounded-lg p-2 h-fit min-w-0 lg:sticky lg:top-4" data-testid="mes-lateral">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setMes(addMonths(mes, -1))} data-testid="mes-prev" className="rounded p-1 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <p className="text-sm font-semibold capitalize text-slate-800" data-testid="mes-titulo">{format(mes, "MMMM yyyy", { locale: es })}</p>
        <button type="button" onClick={() => setMes(addMonths(mes, 1))} data-testid="mes-next" className="rounded p-1 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[9px] font-semibold uppercase text-slate-400 mb-0.5">{["L", "M", "X", "J", "V", "S", "D"].map((x) => <span key={x}>{x}</span>)}</div>
      <div className="grid grid-cols-7 gap-0.5">
        {dias.map((d) => <Dia key={ymd(d)} d={d} mes={mes.getMonth()} sel={ymd(d) === ymd(date)} data={data[ymd(d)]} profs={profs} onPick={onPick} onDrop={onDrop} />)}
      </div>
      <p className="mt-2 text-[10px] text-slate-500">En cada día: citas / huecos libres. Arrastra una cita o un hueco de la agenda a un día para pasarlo allí.</p>
      <div className="mt-3 border-t border-slate-100 pt-2" data-testid="mes-detalle">
        <p className="text-xs font-semibold text-slate-700 mb-1 capitalize">{format(date, "EEEE d MMM", { locale: es })}</p>
        {profs.filter((p) => sel[p.id]).map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-0.5 text-[11px]" data-testid={`mes-detalle-${p.id}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
            <span className="flex-1 truncate text-slate-700">{p.nombre.split(" ")[0]}{p.nombre.includes("Alcobendas") ? " Alc." : ""}</span>
            <span className="text-slate-800 font-semibold">{sel[p.id].citas}c</span>
            <span className="text-emerald-700 font-semibold">{sel[p.id].libres}l</span>
          </div>
        ))}
        {!profs.some((p) => sel[p.id]) && <p className="text-[11px] text-slate-400">Sin citas ni huecos</p>}
      </div>
    </aside>
  );
};
